// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Editor Types — Central type definitions for the Video Editor
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export type CanvasAspect = '16:9' | '9:16' | '1:1' | '4:3' | '3:4';
export type ClipFit = 'contain' | 'cover';
export type ClipTransition = 'none' | 'fade' | 'dissolve';
export type BrightnessCurve = 'none' | 'in' | 'out' | 'both';
export type ClipMotion = 'none' | 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right';
export type ColorGrade = 'none' | 'cinematic' | 'bw';
export type CaptionPosition = 'top' | 'center' | 'bottom';
export type TimelineInsertMode = 'end' | 'playhead';

export const DEFAULT_STILL_DURATION = 4;
export const DEFAULT_FADE_SECONDS = 0.75;
export const DEFAULT_BRIGHTNESS_CURVE_SECONDS = 0.5;

export interface EditorSegment {
  id: string;
  type: 'video' | 'ai_generated' | 'image';
  blob: Blob;
  blobUrl: string;           // URL.createObjectURL result
  duration: number;          // Effective duration after trim
  originalDuration: number;
  speed: number;
  trimStart: number;
  trimEnd: number;
  label: string;
  thumbnailUrl: string;
  /** CDN/gallery URL when the clip came from IA or galeria — never a blob: URL. */
  sourceUrl?: string;
  mediaAssetId?: string;
  /** Prompt/meta of a SEU.VIDEO generation. Imported files stay empty. */
  generationContext?: string;
  volume: number;            // 0 to 1
  muted: boolean;
  fit: ClipFit;
  transitionOut: ClipTransition;
  motion: ClipMotion;
  /** Ease brightness at clip edges so cuts are not a hard pop. */
  brightnessCurve?: BrightnessCurve;
  brightnessCurveSeconds?: number;
}

export type AudioTrackKind = 'voice' | 'sfx' | 'music' | 'import';

export interface AudioTrack {
  id: string;
  blob: Blob;
  blobUrl: string;
  name: string;
  duration: number;
  volume: number;            // 0 to 1
  startOffset: number;       // Where it begins on timeline (seconds)
  trimStart: number;
  trimEnd: number;
  loop: boolean;
  muted?: boolean;
  /** Playback rate for overlay audio (1 = original). Used in preview and export. */
  speed?: number;
  kind?: AudioTrackKind;
  /** CDN URL of TTS/imported audio — never a blob: URL. */
  sourceUrl?: string;
  /** Prompt/meta of a SEU.VIDEO audio generation. */
  generationContext?: string;
}

export interface MediaItem {
  id: string;
  blob: Blob;
  blobUrl: string;
  name: string;
  type: 'video' | 'image' | 'audio';
  duration: number;          // 0 for images
  thumbnailUrl: string;
  addedAt: number;
  sourceUrl?: string;
  mediaAssetId?: string;
  generationContext?: string;
}

export interface ExportSettings {
  resolution: '720p' | '1080p';
  fps: number;
  format: 'mp4';
  aspect: CanvasAspect;
  colorGrade: ColorGrade;
}

export interface HistoryEntry {
  segments: EditorSegment[];
  audioTracks: AudioTrack[];
  captions: Caption[];
}

export interface Caption {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  isImpactPhrase: boolean;
  position?: CaptionPosition;
}

export interface CaptionsSettings {
  textColor: string;
  bgColor: string;
  bgOpacity: number;
  impactTextColor: string;
  fontFamily: string;
  fontSize: number;
  position: CaptionPosition;
}

export function defaultClipFields(
  kind: 'video' | 'image' = 'video',
): Pick<EditorSegment, 'speed' | 'trimStart' | 'trimEnd' | 'volume' | 'muted' | 'fit' | 'transitionOut' | 'motion' | 'brightnessCurve' | 'brightnessCurveSeconds'> {
  return {
    speed: 1,
    trimStart: 0,
    trimEnd: 0,
    volume: 1,
    muted: false,
    fit: 'cover',
    transitionOut: 'dissolve',
    motion: kind === 'image' ? 'zoom-in' : 'none',
    brightnessCurve: 'none',
    brightnessCurveSeconds: DEFAULT_BRIGHTNESS_CURVE_SECONDS,
  };
}

export function defaultExportSettings(): ExportSettings {
  return { resolution: '1080p', fps: 30, format: 'mp4', aspect: '16:9', colorGrade: 'cinematic' };
}

export function defaultCaptionsSettings(): CaptionsSettings {
  return {
    textColor: '#ffffff',
    impactTextColor: '#fbbf24',
    bgColor: '#000000',
    bgOpacity: 0.5,
    fontFamily: 'Inter',
    fontSize: 45,
    position: 'bottom',
  };
}

export function captionYExpr(position: CaptionPosition = 'bottom'): string {
  if (position === 'top') return 'h*0.08';
  if (position === 'center') return '(h-text_h)/2';
  return '(h-text_h)*0.85';
}

// AI Integration types (reused from existing flow)
export interface StoryboardPhase {
  label: string;
  description: string;
  prompt_en?: string;
  /** 1-based index into the reference images sent to Gemini / the video model. */
  image_index?: number;
  image_url?: string;
  loading?: boolean;
}

export interface PreviewItem {
  id: string;
  label: string;
  image_b64: string;
  prompt: string;
}

export type AIStep = 
  | 'idle' 
  | 'frame_captured' 
  | 'previews_ready' 
  | 'preview_selected' 
  | 'generating_video' 
  | 'video_ready';

// Utility
export const generateId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const formatTime = (s: number): string => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const formatTimePrecise = (s: number): string => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 10);
  return `${m}:${sec.toString().padStart(2, '0')}.${ms}`;
};
